/**
 * exportAnimationOnly — Artifact B of the World delivery contract
 * (comms-harryhausen-world-delivery.md §3; ADR-016's clip half of the seam):
 * kernel clips leave as a self-contained animation-only binary GLB — the target
 * rig's full skeleton as PLAIN nodes (rest-pose TRS, no mesh, no skin, no
 * materials) plus the clips baked as glTF animations. World binds by NAME to
 * its own copy of the avatar; the in-file skeleton exists because glTF
 * animation channels must target in-file nodes — it is addressing, not payload.
 *
 * The skeleton is built from the RigDescriptor's localBind — pose-independent
 * BY SOURCE, so unlike exportGlb this door never needs the caller to present
 * rests first (v2's live-TRS-poisoned-the-rest-pose lesson, closed structurally
 * rather than by discipline). Track values and localBind live in the same
 * loaded-scene space, so the written skeleton is exactly the rig the clips
 * were retargeted against.
 *
 * Delivery decisions this module bakes in (ratified in the comms thread,
 * 2026-07-26/27):
 * - Clips must come FROM THE RETARGETED ClipData where one exists — never
 *   re-derived from authored rotation-only keys. Root translation rides along
 *   (World measured the death clip: the single Hips translation channel IS the
 *   fall); stripping locomotion root motion is the CONSUMER's per-clip-class
 *   policy, never done here.
 * - Grafted bones are written under CANONICAL names (`X_graft` → `X`), the
 *   same contract as exportGlb: the delivered clip must name the bones of the
 *   grafted avatar World holds.
 * - 🚨 LICENSING WALL (ADR-016 §5b, two-door model): this is the `export` door —
 *   a file for a human, the creator path (their model, their imported clip).
 *   The `deliver` door (machine-to-machine transfer to poqpoq) is a SEPARATE,
 *   differently-gated function that does not exist yet — do NOT grow this one
 *   into it, and do NOT wire this door up to serve poqpoq's acquired clip
 *   library (Mixamo/CMU are in-world-playback-only; a raw-download affordance
 *   inverts the whole posture — ANIMATION_SOURCE_LICENSING.md §1).
 *
 * Fail-loud line (LAWS): GLTFExporter's own posture on an unresolvable track is
 * console.warn-and-skip. Every track is therefore pre-validated against the
 * rig's bone set and any miss refuses the whole export with the track named.
 * Morph tracks refuse: an animation-only file has no mesh to carry them —
 * morphs deliver with the model, never smuggled into a clip file.
 *
 * Depends on: three (+ GLTFExporter addon), kernel (ClipData, RigDescriptor),
 * to-three-clip, glb-internals. Depended on by: app export door.
 */
import { Group, Object3D } from 'three';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import type { ClipData, RigDescriptor } from '@harryhausen/kernel';
import { toThreeClip } from './to-three-clip.js';
import { GlbExportError, glbJson, installFileReaderShim } from './glb-internals.js';

export interface ExportAnimationOnlyOptions {
  /** Kernel clips to bake — retargeted ClipData (rotation + translation), non-empty. */
  readonly clips: readonly ClipData[];
}

export interface ExportAnimationOnlyResult {
  readonly glb: ArrayBuffer;
  /** Baked animations as written: name + track count, all resolved by name. */
  readonly animations: readonly { name: string; tracks: number }[];
  /** Grafted bones written under their canonical names (`X_graft` → `X`). */
  readonly renamed: readonly { from: string; to: string }[];
  /** Skeleton nodes written (rig bones + a wrapper node iff the rig is multi-root). */
  readonly nodes: number;
}

const stripGraft = (name: string): string => (name.endsWith('_graft') ? name.slice(0, -'_graft'.length) : name);

/**
 * Export `rig`'s skeleton (plain nodes, rest TRS from localBind) with
 * `options.clips` baked, as an animation-only binary GLB. See the module
 * header for the full contract. Throws {@link GlbExportError}; never returns
 * a partial file.
 */
export async function exportAnimationOnly(
  rig: RigDescriptor,
  options: ExportAnimationOnlyOptions,
): Promise<ExportAnimationOnlyResult> {
  const issues: string[] = [];
  if (options.clips.length === 0) {
    throw new GlbExportError(['no clips given — an animation-only file with zero animations is nothing']);
  }

  // --- canonical-name plan over the DESCRIPTOR (the session names stay untouched) ---
  const sessionNames = new Set(rig.bones.map((b) => b.name));
  if (sessionNames.size !== rig.bones.length) {
    throw new GlbExportError(['the rig descriptor carries duplicate bone names — cannot address tracks by name']);
  }
  const renamed: { from: string; to: string }[] = [];
  for (const b of rig.bones) {
    if (!b.name.endsWith('_graft')) continue;
    const to = stripGraft(b.name);
    if (sessionNames.has(to)) {
      // A rig already carrying the canonical name did not need a graft under a
      // suffix — a wiring surprise, refused, never a silent duplicate-name file.
      issues.push(`cannot canonicalize "${b.name}" — the rig already has a bone named "${to}"`);
    }
    renamed.push({ from: b.name, to });
  }

  // --- every track must name a rig bone; morphs cannot ride an animation-only file ---
  const canonicalNames = new Set(rig.bones.map((b) => stripGraft(b.name)));
  for (const clip of options.clips) {
    for (const t of clip.tracks) {
      if (t.path === 'morph') {
        issues.push(
          `clip "${clip.name}": morph track "${t.target}" cannot ride an animation-only file — ` +
            `there is no mesh to target; morphs deliver with the model`,
        );
      } else if (!canonicalNames.has(stripGraft(t.target))) {
        issues.push(`clip "${clip.name}": ${t.path} track targets "${t.target}" — no such bone in the rig`);
      }
    }
  }
  if (issues.length > 0) throw new GlbExportError(issues);

  // --- the skeleton, from localBind: pose-independent by source ---
  const nodeBySessionName = new Map<string, Object3D>();
  for (const b of rig.bones) {
    const node = new Object3D();
    node.name = stripGraft(b.name);
    node.position.set(b.localBind.position.x, b.localBind.position.y, b.localBind.position.z);
    node.quaternion.set(b.localBind.rotation.x, b.localBind.rotation.y, b.localBind.rotation.z, b.localBind.rotation.w);
    // Unit-quat hygiene: float64 residue (|q| = 1 + ~1ulp) fails the spec's
    // [-1,1] range at the validator; normalizing is the identity, not a pose change.
    node.quaternion.normalize();
    node.scale.set(b.localBind.scale.x, b.localBind.scale.y, b.localBind.scale.z);
    nodeBySessionName.set(b.name, node);
  }
  const roots: Object3D[] = [];
  for (const b of rig.bones) {
    const node = nodeBySessionName.get(b.name)!;
    if (b.parent === null) {
      roots.push(node);
    } else {
      const parent = nodeBySessionName.get(b.parent);
      if (parent === undefined) {
        issues.push(`bone "${b.name}" parents to "${b.parent}" — no such bone in the descriptor`);
      } else {
        parent.add(node);
      }
    }
  }
  if (issues.length > 0) throw new GlbExportError(issues);
  if (roots.length === 0) throw new GlbExportError(['the rig descriptor has no root bone — a parent cycle?']);
  // GLTFExporter resolves animation tracks from the FIRST input object, so a
  // multi-root rig needs one wrapper node all roots hang from. Single-root rigs
  // (the normal case) export the root bone directly — no synthetic node.
  let exportRoot: Object3D;
  if (roots.length === 1) {
    exportRoot = roots[0]!;
  } else {
    exportRoot = new Group();
    exportRoot.name = 'AnimationRig';
    for (const r of roots) exportRoot.add(r);
  }

  const clips = options.clips.map((c) =>
    toThreeClip({ ...c, tracks: c.tracks.map((t) => ({ ...t, target: stripGraft(t.target) })) }),
  );

  const uninstallShim = installFileReaderShim();
  let glb: ArrayBuffer;
  try {
    const out = await new GLTFExporter().parseAsync(exportRoot, { binary: true, animations: clips });
    if (!(out instanceof ArrayBuffer)) {
      throw new GlbExportError(['GLTFExporter returned non-binary output despite binary:true']);
    }
    glb = out;
  } finally {
    uninstallShim?.();
  }

  // --- structural asserts on the actual bytes: the animation-only GUARANTEE ---
  const json = glbJson(glb);
  const sceneCount = json.scenes?.length ?? 0;
  if (sceneCount !== 1) {
    throw new GlbExportError([`exported GLB has ${sceneCount} scenes — the contract is exactly one`]);
  }
  const writtenAnimations = json.animations?.length ?? 0;
  if (writtenAnimations !== clips.length) {
    throw new GlbExportError([
      `asked to bake ${clips.length} animation(s) but the file carries ${writtenAnimations} — ` +
        `a track was dropped between validation and write`,
    ]);
  }
  for (const section of ['meshes', 'skins', 'materials', 'textures', 'images'] as const) {
    if ((json[section]?.length ?? 0) > 0) {
      throw new GlbExportError([
        `animation-only export wrote a "${section}" section — the skeleton build leaked model payload`,
      ]);
    }
  }

  return {
    glb,
    animations: clips.map((c) => ({ name: c.name, tracks: c.tracks.length })),
    renamed,
    nodes: rig.bones.length + (roots.length > 1 ? 1 : 0),
  };
}
